/**
 * Delivery Preference Toggle
 * Default: pickup (business is more profitable with pickups)
 * Sets cart attribute `delivery_preference` read by Shopify Functions at checkout
 */
class DeliveryPreferenceToggle extends HTMLElement {
  constructor() {
    super();
    this.buttons = this.querySelectorAll('[data-delivery-option]');
    this.messageEl = this.querySelector('[data-delivery-message]');
  }

  connectedCallback() {
    this.buttons.forEach(btn => {
      btn.addEventListener('click', this.onSelect.bind(this));
    });
    this.initPreference();
  }

  async initPreference() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const pref = cart.attributes?.delivery_preference;

      if (pref) {
        this.setActiveState(pref, false);
      } else {
        await this.writePreference('pickup');
        this.setActiveState('pickup', false);
        this.refreshCart();
      }
    } catch (e) {
      this.setActiveState('pickup', false);
    }
  }

  async onSelect(event) {
    const btn = event.currentTarget;
    const option = btn.dataset.deliveryOption;
    if (btn.classList.contains('is-active')) return;

    this.setActiveState(option, true);

    try {
      await this.writePreference(option);
      document.dispatchEvent(
        new CustomEvent('delivery:preference-changed', { detail: { preference: option } })
      );
      this.refreshCart();
    } catch (e) {
      console.error('Failed to update delivery preference:', e);
      this.setActiveState(option === 'pickup' ? 'delivery' : 'pickup', false);
    }
  }

  async writePreference(option) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { delivery_preference: option } })
    });
    if (!res.ok) throw new Error('Cart update failed');
  }

  /**
   * Drawer pages: CartDrawer listens for 'cart:refresh'.
   * /cart page: CartDrawer is not rendered, so nothing listens.
   * Re-render #MainCart-<id> directly via the Section Rendering API instead.
   */
  refreshCart() {
    const mainCart = document.querySelector('main-cart[id^="MainCart-"]');
    if (mainCart) {
      this.refreshMainCart(mainCart);
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  async refreshMainCart(mainCart) {
    const sectionId = mainCart.id.replace('MainCart-', '');
    try {
      const res = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
      if (!res.ok) throw new Error('Section render failed');
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const updated = doc.getElementById(mainCart.id);
      if (updated) mainCart.innerHTML = updated.innerHTML;
    } catch (e) {
      window.location.reload();
    }
  }

  setActiveState(option, animate) {
    this.buttons.forEach(btn => {
      const isActive = btn.dataset.deliveryOption === option;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    if (this.messageEl) {
      const pickupMsg = this.getAttribute('data-pickup-msg') || 'Pickup selected — switch to delivery if you need it shipped';
      const deliveryMsg = this.getAttribute('data-delivery-msg') || 'Standard pricing applies for delivery orders';
      this.messageEl.textContent = option === 'pickup' ? pickupMsg : deliveryMsg;
      this.messageEl.className = 'delivery-toggle__message delivery-toggle__message--' + option;
      this.messageEl.style.display = '';
    }
  }
}

if (!customElements.get('delivery-preference-toggle')) {
  customElements.define('delivery-preference-toggle', DeliveryPreferenceToggle);
}